import { RevealGroup, RevealItem } from '@/components/Reveal';
import { Spine, SpineItem } from '@/components/Spine';
import Typography from '@/components/ui/typography';
import { cn } from '@/lib/utils';

export interface Chapter {
  tagline: string;
  title: string;
  description: string;
}

/*
 * A narrative told as numbered chapters down the spine, image and text swapping
 * sides as it goes.
 *
 * Five About pages carried this same shape as five copies of the same markup,
 * each one tilting its photographs with rotate-6/-rotate-6 — the most dated
 * thing left on the site. The tilt is gone; the images are cropped to a common
 * aspect instead, which is what stops a row of mismatched source photographs
 * from reading as clutter.
 */
const Chapters = ({
  chapters,
  images = [],
  className,
}: {
  chapters: Chapter[];
  /**
   * Resolved image URLs, aligned to `chapters` by index. Shorter is fine —
   * a chapter with no image renders as full-width prose rather than as a
   * broken <img>, which is what positional indexing used to produce whenever
   * a directory held fewer files than the page assumed.
   */
  images?: string[];
  className?: string;
}) => (
  <Spine className={className}>
    {chapters.map((chapter, index) => {
      const image = images[index];
      const flipped = index % 2 === 1;

      return (
        <SpineItem
          key={chapter.title}
          marker={String(index + 1).padStart(2, '0')}
        >
          <RevealGroup
            className={cn(
              'grid items-center gap-6 md:gap-10',
              image && 'md:grid-cols-2'
            )}
          >
            <RevealItem
              className={cn(
                'flex flex-col items-start gap-3',
                flipped && 'md:order-2'
              )}
            >
              <Typography.Eyebrow>{chapter.tagline}</Typography.Eyebrow>
              <Typography.Display3 className="text-2xl md:text-3xl lg:text-4xl">
                {chapter.title}
              </Typography.Display3>
              <Typography.Body className="max-w-prose text-muted-foreground">
                {chapter.description}
              </Typography.Body>
            </RevealItem>

            {image && (
              <RevealItem className={cn(flipped && 'md:order-1')}>
                <div className="aspect-4/3 overflow-hidden rounded-xl bg-muted shadow-card">
                  <img
                    className="size-full object-cover"
                    src={image}
                    alt=""
                    loading="lazy"
                  />
                </div>
              </RevealItem>
            )}
          </RevealGroup>
        </SpineItem>
      );
    })}
  </Spine>
);

export default Chapters;
